import LoadingAtom from "@/components/loadingAtom/LoadingAtom";
import { CENTER_TOP } from "@/components/vault/viewer/backgrounds/ThumbnailCaptureBackground";
import { LoaderData } from "@/components/vault/viewer/VaultObjectViewerModal";
import useVaultStore, {
  useVaultStoreActions,
} from "@/stores/zustand/vault/useVaultStore";
import { DEFAULT_AZIMUTH_ANGLE, DEFAULT_POLAR_ANGLE } from "@/utils/cameraUtil";
import { css } from "@emotion/react";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { ICON as ICON_v1_1 } from "@henge-inc/ui/v1_1";
import ThumbnailCaptureBoundaryImage from "@public/assets/images/capture/Boundary.webp";
import { AnimatePresence, motion } from "motion/react";
import NextImage from "next/image";
import React from "react";

interface SubmitStickerProps {
  loaderData: LoaderData | null;
  canvasStatus: "idle" | "loading" | "render" | "error" | "hidden";
  wrapperSize: { width: number; height: number } | undefined;
  setCurrentTransformControlsMode: React.Dispatch<
    React.SetStateAction<"rotate" | "translate" | null>
  >;
  captureReady: boolean;
  uploadLoading: boolean;
  setUploadLoading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadComplete: boolean;
  setUploadComplete: React.Dispatch<React.SetStateAction<boolean>>;
  uploadFailed: boolean;
  setUploadFailed: React.Dispatch<React.SetStateAction<boolean>>;
  warnThumbnailClipping: boolean;
}

export const SubmitSticker = ({
  loaderData,
  canvasStatus,
  wrapperSize,
  setCurrentTransformControlsMode,
  captureReady,
  uploadLoading,
  setUploadLoading,
  uploadComplete,
  setUploadComplete,
  uploadFailed,
  setUploadFailed,
  warnThumbnailClipping,
}: SubmitStickerProps) => {
  const currentPage = useVaultStore((state) => state.currentPage);
  const {
    nextSizeCaptureVaultObjectSizeConfig,
    uploadViewingVaultObject,
    setCurrentPage,
    closeVaultObjectViewer,
  } = useVaultStoreActions();

  if (uploadLoading || uploadComplete || uploadFailed) {
    return null;
  }
  return (
    <div
      className={
        "pointer-events-none absolute inset-0 rounded-[20px] overflow-hidden z-20"
      }
    >
      <div
        style={{
          width: wrapperSize ? (wrapperSize.height * 16) / 10 : 0,
          height: wrapperSize ? wrapperSize.height : 0,
        }}
        className={
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        }
      >
        {
          <>
            <NextImage
              src={ThumbnailCaptureBoundaryImage}
              width={0}
              height={0}
              sizes={"100vw"}
              alt={`${currentPage} background image`}
              loading={"eager"}
              priority={true}
              className="absolute inset-0 z-0"
              style={{
                opacity: warnThumbnailClipping ? 1 : 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "opacity 0.3s",
              }}
            />

            {/* Move */}
            <div
              style={{
                width: "auto",
                height: "5%",
                aspectRatio: "1 / 1",
                position: "absolute",
                top: `${CENTER_TOP + 16.640625}%`,
                left: `${50 + 8.30078125}%`,
              }}
              className={"-translate-x-1/2 -translate-y-1/2"}
              onClick={() => {
                setCurrentTransformControlsMode((current) =>
                  current === "translate" ? null : "translate",
                );
              }}
            >
              <div
                style={{
                  width: "auto",
                  height: "75%",
                  aspectRatio: "1 / 1",
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute bottom-1/2 left-1/2 -translate-x-1/2 rotate-45 rounded-lg overflow-hidden"
                }
              />
              <div
                style={{
                  width: "auto",
                  height: "120%",
                  aspectRatio: "1 / 1",
                  transform: `translate(-50%, -50%) rotateX(${
                    (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                  }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md overflow-hidden"
                }
              />
            </div>
            {/* Scale */}
            <div
              style={{
                width: "auto",
                height: "5%",
                aspectRatio: "1 / 1",
                position: "absolute",
                top: `${CENTER_TOP + 11.875}%`,
                left: `${50 + 13.330078125}%`,
              }}
              className={"-translate-x-1/2 -translate-y-1/2"}
              onClick={() => {
                setCurrentTransformControlsMode(null);
                nextSizeCaptureVaultObjectSizeConfig();
              }}
            >
              <div
                style={{
                  width: "auto",
                  height: "75%",
                  aspectRatio: "1 / 1",
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute bottom-1/2 left-1/2 -translate-x-1/2 rotate-45 rounded-lg overflow-hidden"
                }
              />
              <div
                style={{
                  width: "auto",
                  height: "120%",
                  aspectRatio: "1 / 1",
                  transform: `translate(-50%, -50%) rotateX(${
                    (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                  }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md overflow-hidden"
                }
              />
            </div>
            {/* Rotation */}
            <div
              style={{
                width: "auto",
                height: "5%",
                aspectRatio: "1 / 1",
                position: "absolute",
                top: `${CENTER_TOP + 7.265625}%`,
                left: `${50 + 18.45703125}%`,
              }}
              className={"-translate-x-1/2 -translate-y-1/2"}
              onClick={() => {
                setCurrentTransformControlsMode((current) =>
                  current === "rotate" ? null : "rotate",
                );
              }}
            >
              <div
                style={{
                  width: "auto",
                  height: "75%",
                  aspectRatio: "1 / 1",
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute bottom-1/2 left-1/2 -translate-x-1/2 rotate-45 rounded-lg overflow-hidden"
                }
              />
              <div
                style={{
                  width: "auto",
                  height: "120%",
                  aspectRatio: "1 / 1",
                  transform: `translate(-50%, -50%) rotateX(${
                    (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                  }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                }}
                className={
                  "pointer-events-auto cursor-pointer absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md overflow-hidden"
                }
              />
            </div>
          </>
        }
      </div>

      {/* Clipping Warning */}
      <AnimatePresence>
        {warnThumbnailClipping && (
          <motion.div
            className={"absolute bottom-14 left-1/2 -translate-x-1/2"}
            initial={{
              opacity: 0,
            }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
            }}
          >
            <div
              style={{
                backgroundColor: "var(--indigo200)",
                filter: "opacity(0.2) blur(18px)",
              }}
              className={
                "pointer-events-none absolute inset-0 rounded-lg z-[-1]"
              }
            />
            <HUI.Text
              kind={HENGE.TEXT_KIND.body2}
              align={HENGE.TEXT_ALIGN.center}
              whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
            >
              Thumbnail may be cut off.
            </HUI.Text>
            <HUI.Text
              kind={HENGE.TEXT_KIND.body2}
              align={HENGE.TEXT_ALIGN.center}
              whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
            >
              Adjust the object into the boundary.
            </HUI.Text>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute top-0 left-1/2 flex flex-col z-10 -translate-x-1/2 p-6 w-full bg-gradient-to-b from-white from-50% to-transparent">
        <HUI.Text
          kind={HENGE.TEXT_KIND.heading3}
          weight={HENGE.TEXT_WEIGHT.semibold}
          align={HENGE.TEXT_ALIGN.center}
        >
          Create Thumbnail
        </HUI.Text>
        <HUI.Text
          kind={HENGE.TEXT_KIND.body2}
          weight={HENGE.TEXT_WEIGHT.medium}
          colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
          align={HENGE.TEXT_ALIGN.center}
        >
          This is the last step. Adjust the thumbnail image.
        </HUI.Text>
      </div>

      <div className="w-full h-full flex flex-col flex-nowrap justify-center items-center">
        {!captureReady && <LoadingAtom width={160} height={160} />}
      </div>

      <div
        style={{
          width: "100%",
        }}
        className="pointer-events-none absolute flex flex-row items-center justify-end gap-2 p-3 bottom-0 bg-gradient-to-t from-white to-transparent"
        css={css`
          & > button {
            pointer-events: auto;
          }
        `}
      >
        <HUI.Button
          kind={HENGE.BUTTON_KIND.borderless}
          colour={HENGE.BUTTON_COLOUR.gray}
          icon={{
            icon: ICON_v1_1.ChevronLeft,
            size: HENGE.DETAILED_SIZE.medium,
            kind: HENGE.ICON_KIND.regular,
          }}
          onClick={() => {
            setCurrentPage("uploader");
          }}
        />
        <div className="w-px h-4 bg-[var(--border-secondary)] flex-shrink-0 mx-2" />
        <HUI.Button
          kind={HENGE.BUTTON_KIND.filled}
          colour={HENGE.THEME_COLOUR.mono}
          disabled={!captureReady}
          loading={uploadLoading}
          onClick={async () => {
            if (currentPage === "uploader-thumbnail") {
              await uploadViewingVaultObject(
                () => setUploadLoading(true),
                () => setUploadLoading(false),
                () => {
                  setUploadComplete(true);
                  setTimeout(() => {
                    closeVaultObjectViewer();
                  }, 1500);
                },
                () => setUploadFailed(true),
              );
            }
          }}
        >
          Upload
        </HUI.Button>
      </div>
    </div>
  );
};
